// src/hooks/useCustomers.js - Hook replacement that uses CustomerStore
import useCustomerStore from '../stores/CustomerStore';
import { useSocket } from '../contexts/SocketContext';

/**
 * Custom hook for accessing customer data from CustomerStore
 * 
 * UPDATED: Now uses Zustand CustomerStore instead of CustomersProvider
 * Store initialization is handled at app level in SocketContext.
 * 
 * FIXED: Uses proper Zustand selectors instead of destructuring entire store
 * 
 * @returns {object} Customer data and operations
 */
export const useCustomers = () => {
  const socket = useSocket();
  
  // 🚨 FIXED: Use individual selectors instead of destructuring entire store
  // This ensures React re-renders when specific properties change
  
  // Customer list data
  const customers = useCustomerStore(state => state.customers);
  const customersLoading = useCustomerStore(state => state.customersLoading);
  const customersError = useCustomerStore(state => state.customersError);
  const customersPagination = useCustomerStore(state => state.customersPagination);
  
  // Customer search data
  const customerSearchResults = useCustomerStore(state => state.customerSearchResults);
  const customerSearchLoading = useCustomerStore(state => state.customerSearchLoading);
  
  // Selected customer data (complete with appointments & invoices)
  const selectedCustomer = useCustomerStore(state => state.selectedCustomer);
  const selectedCustomerLoading = useCustomerStore(state => state.selectedCustomerLoading);
  const selectedCustomerError = useCustomerStore(state => state.selectedCustomerError);

  // Granular saving flags
  const creatingCustomer = useCustomerStore(state => state.creatingCustomer);
  const savingCustomer = useCustomerStore(state => state.savingCustomer);
  const deletingCustomer = useCustomerStore(state => state.deletingCustomer);
  
  // Customer operations (functions don't change, so we can get them once)
  const loadCustomers = useCustomerStore(state => state.loadCustomers);
  const searchCustomers = useCustomerStore(state => state.searchCustomers);
  const loadCustomerDetails = useCustomerStore(state => state.loadCustomerDetails);
  const createCustomer = useCustomerStore(state => state.createCustomer);
  const updateCustomer = useCustomerStore(state => state.updateCustomer);
  const deleteCustomer = useCustomerStore(state => state.deleteCustomer);
  
  // Utility functions
  const clearCustomersError = useCustomerStore(state => state.clearCustomersError);
  const clearSelectedCustomer = useCustomerStore(state => state.clearSelectedCustomer);
  const refreshCustomers = useCustomerStore(state => state.refreshCustomers);
  const getCustomerById = useCustomerStore(state => state.getCustomerById);
  
  // Connection state
  const isConnected = useCustomerStore(state => state.isConnected);
  
  // Internal initialization state (for fallback check)
  const _initialized = useCustomerStore(state => state._initialized);
  
  // Fallback initialization check (safety net)
  // If app-level initialization failed but we're authenticated, try fallback
  if (socket.isAuthenticated && !socket.storesInitialized && !_initialized) {
    console.warn('⚠️ useCustomers: Store not initialized at app level, trying fallback');
    socket.initializeStore('CustomerStore');
  }
  
  // Return the exact same interface as the old CustomersProvider
  return {
    // Customer list data
    customers,
    customersLoading,
    customersError,
    customersPagination,
    
    // Customer search data  
    customerSearchResults,
    customerSearchLoading,
    
    // Selected customer data (complete with appointments & invoices)
    selectedCustomer,
    selectedCustomerLoading,
    selectedCustomerError,

    // Granular saving flags
    creatingCustomer,
    savingCustomer,
    deletingCustomer,

    // Customer operations
    loadCustomers,
    searchCustomers,
    loadCustomerDetails,
    createCustomer,
    updateCustomer,
    deleteCustomer,
    
    // Utility functions
    clearCustomersError,
    clearSelectedCustomer,
    refreshCustomers,
    getCustomerById,
    
    // Connection state
    isConnected: socket.isAuthenticated,
    
    // Legacy aliases for backward compatibility
    loading: customersLoading,
    searchLoading: customerSearchLoading,
    error: customersError,
    pagination: customersPagination,
    searchResults: customerSearchResults,
    clearError: clearCustomersError
  };
};

export default useCustomers;